import { FC, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FlexBox, useBreakpoint, useLockBodyScroll } from '@brg/ui';
import { LoaderSpinner } from '../../modals/artzLegalsModal/LoaderAnimation';
import { LongreadBlockRenderer, CoverSlide } from './components';
import { useGetLongreadBlocks } from './hooks';
import { PRESENTATION_STYLES, SLIDER_SETTINGS, SLIDER_INIT_TIMEOUT } from './constants';
import { preparePresentationBlocks } from './utils/blockUtils';
import { LayoutConst } from 'core';
import { ModalClose } from 'core/modules/modal.module';
import { CloseModalIcon } from 'core/modules/CloseIconNew';
import { CloseModalIconMob } from 'core/modules/CloseIconNewMob';
import {
  PresentationModeContainer,
  SliderContainer,
  SlideWrapper,
  BlockContainer,
  EmptyStateContent,
  ExitPresentationButton,
  ButtonsWrapper,
  FullscreenImageOverlay,
  FullscreenImage,
  DisablePullToRefresh,
  HeightAdjustmentStyles,
} from './styles/PresentationModeModal.styles';
import { Sidebar } from './components/Sidebar';
import { Navigation } from './components/Navigation';
import { PresentationModeModalProps } from './types';

export const PresentationModeModal: FC<PresentationModeModalProps> = ({ backgroundColor, project, customLGId, isTeam, close }) => {
  const sliderRef = useRef<Slider>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const isInitializedFromUrlRef = useRef(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [fullscreenImageSrc, setFullscreenImageSrc] = useState<string | null>(null);
  const [fullscreenImageAlt, setFullscreenImageAlt] = useState('');
  const { blocks, isLoading } = useGetLongreadBlocks(project, customLGId);
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobile744 = useBreakpoint(LayoutConst.screen.s);
  const history = useHistory();
  const location = useLocation();
  
  useLockBodyScroll(true);

//стабильный свайп между слайдами
  useEffect(() => {
    const el = sliderContainerRef.current;
    if (!el || !isMobile744) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (
        touchStartYRef.current === null ||
        touchStartXRef.current === null ||
        e.touches.length !== 1
      ) return;

      const touch = e.touches[0];
      const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
      const deltaX = Math.abs(touch.clientX - touchStartXRef.current);

      if (deltaY > deltaX && deltaY > 8) {
        e.preventDefault();
      }
    };

    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [isMobile744]);

  const presentationBlocks = useMemo(() => {
    return preparePresentationBlocks(blocks, PRESENTATION_STYLES);
  }, [blocks]);

  const currentBlock = useMemo(() => {
    if (currentSlide > 0 && currentSlide <= presentationBlocks.length) {
      return presentationBlocks[currentSlide - 1];
    }
    return null;
  }, [currentSlide, presentationBlocks]);

  const currentBlockPostId = useMemo(() => {
    return currentBlock?.postId || currentBlock?.id;
  }, [currentBlock]);

  const getSlideFromHash = useCallback((): number | null => {
    const hash = location.hash || window.location.hash;
    if (!hash) return null;
    
    const match = hash.match(/[#&]slide=(\d+)/);
    if (match) {
      const slideNumber = parseInt(match[1], 10);
      return isNaN(slideNumber) ? null : slideNumber;
    }
    return null;
  }, [location.hash]);

  const isHashUpdateProgrammaticRef = useRef(false);

  const setSlideInHash = useCallback((slideIndex: number, totalSlides: number) => {
    if (slideIndex < 0 || slideIndex >= totalSlides) return;
    
    const currentHash = location.hash || window.location.hash || '';
    const hashWithoutSlide = currentHash
      .replace(/[#&]slide=\d+/g, '')
      .replace(/^#/, '')
      .replace(/^&/, '');
    
    const slideParam = `slide=${slideIndex}`;
    let newHash = '';
    
    if (hashWithoutSlide) {
      newHash = `#${hashWithoutSlide}&${slideParam}`;
    } else {
      newHash = `#${slideParam}`;
    }
    
    if (location.hash !== newHash) {
      isHashUpdateProgrammaticRef.current = true;
      history.replace({
        ...location,
        hash: newHash,
      });

      setTimeout(() => {
        isHashUpdateProgrammaticRef.current = false;
      }, 100);
    }
  }, [history, location]);

  const getCurrentSlideInfo = useCallback(() => {
    return {
      slideIndex: currentSlide,
      blockPostId: currentBlockPostId,
    };
  }, [currentSlide, currentBlockPostId]);

  const handleShrinkClick = () => {
    if (close) {
      close(getCurrentSlideInfo());
    }
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handleSlideClick = (e: React.MouseEvent, slideIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(slideIndex);
    }
  };

  const handleImageClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('button, a, input, textarea, select, video, iframe')) {
      return;
    }

    const imageNode = target.closest('img') as HTMLImageElement | null;
    if (imageNode?.src) {
      e.preventDefault();
      e.stopPropagation();
      setFullscreenImageSrc(imageNode.currentSrc || imageNode.src);
      setFullscreenImageAlt(imageNode.alt || '');
      return;
    }

    const fullscreenContainer = target.closest('[data-fullscreen-image]') as HTMLElement | null;
    const imageInsideContainer = fullscreenContainer?.querySelector('img') as HTMLImageElement | null;
    if (!imageInsideContainer?.src) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setFullscreenImageSrc(imageInsideContainer.currentSrc || imageInsideContainer.src);
    setFullscreenImageAlt(imageInsideContainer.alt || '');
  };

  const handleCloseFullscreen = () => {
    setFullscreenImageSrc(null);
    setFullscreenImageAlt('');
  };

  const handleMobileTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile744 || fullscreenImageSrc || e.touches.length !== 1) {
      return;
    }

    touchStartYRef.current = e.touches[0].clientY;
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleMobileTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile744 || fullscreenImageSrc || touchStartYRef.current === null || touchStartXRef.current === null) {
      return;
    }

    const touch = e.changedTouches[0];
    const deltaY = touch.clientY - touchStartYRef.current;
    const deltaX = touch.clientX - touchStartXRef.current;
    const absDeltaY = Math.abs(deltaY);
    const absDeltaX = Math.abs(deltaX);
    const SWIPE_THRESHOLD = 36;

    touchStartYRef.current = null;
    touchStartXRef.current = null;

    if (absDeltaY < SWIPE_THRESHOLD || absDeltaY < absDeltaX) {
      return;
    }

    if (deltaY < 0) {
      handleNext();
      return;
    }

    handlePrev();
  };


  const totalSlides = presentationBlocks.length + 1;
  const canGoPrev = currentSlide > 0;
  const canGoNext = currentSlide < totalSlides - 1;
  const isInteractiveVideoSlide = currentSlide > 0 && presentationBlocks[currentSlide - 1]?.style === 'video';

  useEffect(() => {
    if (isLoading || presentationBlocks.length === 0 || isInitializedFromUrlRef.current) {
      return;
    }

    const slideFromHash = getSlideFromHash();
    if (slideFromHash !== null && slideFromHash >= 0 && slideFromHash < totalSlides) {
      isInitializedFromUrlRef.current = true;
      setCurrentSlide(slideFromHash);

      setTimeout(() => {
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(slideFromHash);
        }
      }, SLIDER_INIT_TIMEOUT);
    } else {
      const currentHash = location.hash || window.location.hash || '';
      if (!currentHash.match(/[#&]slide=\d+/)) {
        setSlideInHash(0, totalSlides);
      }
      isInitializedFromUrlRef.current = true;
    }
  }, [isLoading, presentationBlocks.length, totalSlides, getSlideFromHash, setSlideInHash, location.hash]);

  useEffect(() => {
    if (!isInitializedFromUrlRef.current || isLoading || presentationBlocks.length === 0) {
      return;
    }

    if (isHashUpdateProgrammaticRef.current) {
      return;
    }

    const slideFromHash = getSlideFromHash();
    
    if (slideFromHash !== null && slideFromHash === currentSlide) {
      return;
    }

    if (currentSlide === 0 && slideFromHash !== null && slideFromHash > 0) {
      return;
    }

    if (slideFromHash !== currentSlide) {
      setSlideInHash(currentSlide, totalSlides);
    }
  }, [currentSlide, totalSlides, isLoading, presentationBlocks.length, setSlideInHash, getSlideFromHash]);

  useEffect(() => {
    if (!isMobile744 || !isSidebarOpen) {
      return;
    }
    setIsSidebarOpen(false);
  }, [isMobile744, isSidebarOpen]);

  useEffect(() => {
    document.querySelectorAll('audio').forEach((audio) => {
      if (!audio.paused) audio.pause();
    });
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fullscreenImageSrc) {
        if (e.key === 'Escape') {
          handleCloseFullscreen();
        }
        return;
      }

      const prevKeys = ['ArrowLeft', 'ArrowUp', 'PageUp'];
      const nextKeys = ['ArrowRight', 'ArrowDown', 'PageDown'];

      if (prevKeys.includes(e.key)) {
        e.preventDefault();
        if (canGoPrev) {
          handlePrev();
        }
        return;
      }

      if (nextKeys.includes(e.key)) {
        e.preventDefault();
        if (canGoNext) {
          handleNext();
        }
        return;
      }

      if (e.key === 'Escape' && close) {
        close(getCurrentSlideInfo());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlide, totalSlides, close, canGoPrev, canGoNext, fullscreenImageSrc, getCurrentSlideInfo, presentationBlocks.length]);

  if (isLoading) {
    return (
      <PresentationModeContainer backgroundColor={backgroundColor} onClick={(e) => e.stopPropagation()}>
        <EmptyStateContent>
          <FlexBox align="center" width={"100%"} justify="center" style={{ height: "calc(50vw - 64px)" }}>
            <LoaderSpinner text={'Загрузка...'} color="#909090" />
          </FlexBox>
          {close && (
            <ExitPresentationButton onClick={close}>
              Выйти из режима презентации
            </ExitPresentationButton>
          )}
        </EmptyStateContent>
      </PresentationModeContainer>
    );
  }

  if (presentationBlocks.length === 0) {
    return (
      <PresentationModeContainer backgroundColor={backgroundColor} onClick={(e) => e.stopPropagation()}>
        <EmptyStateContent>
          <div>Нет блоков для отображения</div>
          {close && (
            <ExitPresentationButton onClick={close}>
              Выйти из режима презентации
            </ExitPresentationButton>
          )}
        </EmptyStateContent>
      </PresentationModeContainer>
    );
  }

  return (
    <PresentationModeContainer backgroundColor={backgroundColor} isSidebarOpen={!isMobile744 && isSidebarOpen} onClick={(e) => e.stopPropagation()}>
      <DisablePullToRefresh />
      <HeightAdjustmentStyles />
      <Sidebar
        isOpen={!isMobile744 && isSidebarOpen}
        backgroundColor={backgroundColor}
        totalSlides={totalSlides}
        currentSlide={currentSlide}
        project={project}
        blocks={blocks}
        presentationBlocks={presentationBlocks}
        theme={theme}
        lang={i18n.language}
        onSlideClick={handleSlideClick}
      />

      <SliderContainer
        ref={sliderContainerRef}
        isSidebarOpen={!isMobile744 && isSidebarOpen}
        onTouchStart={handleMobileTouchStart}
        onTouchEnd={handleMobileTouchEnd}
        onClickCapture={handleImageClickCapture}
      >
        <Slider
          ref={sliderRef}
          {...SLIDER_SETTINGS}
          swipe={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          draggable={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          touchMove={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          afterChange={(index) => setCurrentSlide(index)}
        >
          <SlideWrapper key="cover" isActive={currentSlide === 0}>
            <CoverSlide project={project} blocks={blocks} backgroundColor={backgroundColor} lang={i18n.language} isTeam={isTeam} />
          </SlideWrapper>

          {presentationBlocks.map((block, index) => {
            const slideIndex = index + 1;
            const isActive = currentSlide === slideIndex;
            return (
              <SlideWrapper key={block.postId || block.id || index} isActive={isActive}>
                <BlockContainer>
                  <LongreadBlockRenderer 
                    block={block} 
                    theme={theme} 
                    backgroundColor={backgroundColor} 
                    isActive={isActive}
                  />
                </BlockContainer>
              </SlideWrapper>
            );
          })}
        </Slider>
        <ButtonsWrapper isSidebarOpen={!isMobile744 && isSidebarOpen}>
          <Navigation
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            isMobile={isMobile744}
            onPrev={handlePrev}
            onNext={handleNext}
            onShrink={handleShrinkClick}
            onToggleSidebar={() => !isMobile744 && setIsSidebarOpen(!isSidebarOpen)}
          />
        </ButtonsWrapper>
        {fullscreenImageSrc && (
          <FullscreenImageOverlay onClick={handleCloseFullscreen}>
            <ModalClose
              isPreviewer
              mobileVersion={isMobile744}
              role="button"
              aria-label={t('close')}
              onClick={(e) => {
                e.stopPropagation();
                handleCloseFullscreen();
              }}
            >
              {isMobile744 ? <CloseModalIconMob /> : <CloseModalIcon />}
            </ModalClose>
            <FullscreenImage
              src={fullscreenImageSrc}
              alt={fullscreenImageAlt}
              onClick={(e) => e.stopPropagation()}
            />
          </FullscreenImageOverlay>
        )}
      </SliderContainer>
    </PresentationModeContainer>
  );
};
import { FC, useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { FlexBox, useBreakpoint, useLockBodyScroll } from '@brg/ui';
import { LoaderSpinner } from '../../modals/artzLegalsModal/LoaderAnimation';
import { LongreadBlockRenderer, CoverSlide } from './components';
import { useGetLongreadBlocks } from './hooks';
import { PRESENTATION_STYLES, SLIDER_SETTINGS, SLIDER_INIT_TIMEOUT } from './constants';
import { preparePresentationBlocks } from './utils/blockUtils';
import { LayoutConst } from 'core';
import { ModalClose } from 'core/modules/modal.module';
import { CloseModalIcon } from 'core/modules/CloseIconNew';
import { CloseModalIconMob } from 'core/modules/CloseIconNewMob';
import {
  PresentationModeContainer,
  SliderContainer,
  SlideWrapper,
  BlockContainer,
  EmptyStateContent,
  ExitPresentationButton,
  ButtonsWrapper,
  FullscreenImageOverlay,
  FullscreenImage,
  DisablePullToRefresh,
  HeightAdjustmentStyles,
} from './styles/PresentationModeModal.styles';
import { Sidebar } from './components/Sidebar';
import { Navigation } from './components/Navigation';
import { PresentationModeModalProps } from './types';

export const PresentationModeModal: FC<PresentationModeModalProps> = ({ backgroundColor, project, customLGId, isTeam, close }) => {
  const sliderRef = useRef<Slider>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const isInitializedFromUrlRef = useRef(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [fullscreenImageSrc, setFullscreenImageSrc] = useState<string | null>(null);
  const [fullscreenImageAlt, setFullscreenImageAlt] = useState('');
  const { blocks, isLoading } = useGetLongreadBlocks(project, customLGId);
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobile744 = useBreakpoint(LayoutConst.screen.s);
  const history = useHistory();
  const location = useLocation();
  
  useLockBodyScroll(true);

//стабильный свайп между слайдами
  useEffect(() => {
    const el = sliderContainerRef.current;
    if (!el || !isMobile744) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (
        touchStartYRef.current === null ||
        touchStartXRef.current === null ||
        e.touches.length !== 1
      ) return;

      const touch = e.touches[0];
      const deltaY = Math.abs(touch.clientY - touchStartYRef.current);
      const deltaX = Math.abs(touch.clientX - touchStartXRef.current);

      if (deltaY > deltaX && deltaY > 8) {
        e.preventDefault();
      }
    };

    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [isMobile744]);

  const presentationBlocks = useMemo(() => {
    return preparePresentationBlocks(blocks, PRESENTATION_STYLES);
  }, [blocks]);

  const currentBlock = useMemo(() => {
    if (currentSlide > 0 && currentSlide <= presentationBlocks.length) {
      return presentationBlocks[currentSlide - 1];
    }
    return null;
  }, [currentSlide, presentationBlocks]);

  const currentBlockPostId = useMemo(() => {
    return currentBlock?.postId || currentBlock?.id;
  }, [currentBlock]);

  const getSlideFromHash = useCallback((): number | null => {
    const hash = location.hash || window.location.hash;
    if (!hash) return null;
    
    const match = hash.match(/[#&]slide=(\d+)/);
    if (match) {
      const slideNumber = parseInt(match[1], 10);
      return isNaN(slideNumber) ? null : slideNumber;
    }
    return null;
  }, [location.hash]);

  const isHashUpdateProgrammaticRef = useRef(false);

  const setSlideInHash = useCallback((slideIndex: number, totalSlides: number) => {
    if (slideIndex < 0 || slideIndex >= totalSlides) return;
    
    const currentHash = location.hash || window.location.hash || '';
    const hashWithoutSlide = currentHash
      .replace(/[#&]slide=\d+/g, '')
      .replace(/^#/, '')
      .replace(/^&/, '');
    
    const slideParam = `slide=${slideIndex}`;
    let newHash = '';
    
    if (hashWithoutSlide) {
      newHash = `#${hashWithoutSlide}&${slideParam}`;
    } else {
      newHash = `#${slideParam}`;
    }
    
    if (location.hash !== newHash) {
      isHashUpdateProgrammaticRef.current = true;
      history.replace({
        ...location,
        hash: newHash,
      });

      setTimeout(() => {
        isHashUpdateProgrammaticRef.current = false;
      }, 100);
    }
  }, [history, location]);

  const getCurrentSlideInfo = useCallback(() => {
    return {
      slideIndex: currentSlide,
      blockPostId: currentBlockPostId,
    };
  }, [currentSlide, currentBlockPostId]);

  const handleShrinkClick = () => {
    if (close) {
      close(getCurrentSlideInfo());
    }
  };

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handleSlideClick = (e: React.MouseEvent, slideIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(slideIndex);
    }
  };

  const handleImageClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (target.closest('button, a, input, textarea, select, video, iframe')) {
      return;
    }

    const imageNode = target.closest('img') as HTMLImageElement | null;
    if (imageNode?.src) {
      e.preventDefault();
      e.stopPropagation();
      setFullscreenImageSrc(imageNode.currentSrc || imageNode.src);
      setFullscreenImageAlt(imageNode.alt || '');
      return;
    }

    const fullscreenContainer = target.closest('[data-fullscreen-image]') as HTMLElement | null;
    const imageInsideContainer = fullscreenContainer?.querySelector('img') as HTMLImageElement | null;
    if (!imageInsideContainer?.src) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setFullscreenImageSrc(imageInsideContainer.currentSrc || imageInsideContainer.src);
    setFullscreenImageAlt(imageInsideContainer.alt || '');
  };

  const handleCloseFullscreen = () => {
    setFullscreenImageSrc(null);
    setFullscreenImageAlt('');
  };

  const handleMobileTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile744 || fullscreenImageSrc || e.touches.length !== 1) {
      return;
    }

    touchStartYRef.current = e.touches[0].clientY;
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleMobileTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile744 || fullscreenImageSrc || touchStartYRef.current === null || touchStartXRef.current === null) {
      return;
    }

    const touch = e.changedTouches[0];
    const deltaY = touch.clientY - touchStartYRef.current;
    const deltaX = touch.clientX - touchStartXRef.current;
    const absDeltaY = Math.abs(deltaY);
    const absDeltaX = Math.abs(deltaX);
    const SWIPE_THRESHOLD = 36;

    touchStartYRef.current = null;
    touchStartXRef.current = null;

    if (absDeltaY < SWIPE_THRESHOLD || absDeltaY < absDeltaX) {
      return;
    }

    if (deltaY < 0) {
      handleNext();
      return;
    }

    handlePrev();
  };


  const totalSlides = presentationBlocks.length + 1;
  const canGoPrev = currentSlide > 0;
  const canGoNext = currentSlide < totalSlides - 1;
  const isInteractiveVideoSlide = currentSlide > 0 && presentationBlocks[currentSlide - 1]?.style === 'video';

  useEffect(() => {
    if (isLoading || presentationBlocks.length === 0 || isInitializedFromUrlRef.current) {
      return;
    }

    const slideFromHash = getSlideFromHash();
    if (slideFromHash !== null && slideFromHash >= 0 && slideFromHash < totalSlides) {
      isInitializedFromUrlRef.current = true;
      setCurrentSlide(slideFromHash);

      setTimeout(() => {
        if (sliderRef.current) {
          sliderRef.current.slickGoTo(slideFromHash);
        }
      }, SLIDER_INIT_TIMEOUT);
    } else {
      const currentHash = location.hash || window.location.hash || '';
      if (!currentHash.match(/[#&]slide=\d+/)) {
        setSlideInHash(0, totalSlides);
      }
      isInitializedFromUrlRef.current = true;
    }
  }, [isLoading, presentationBlocks.length, totalSlides, getSlideFromHash, setSlideInHash, location.hash]);

  useEffect(() => {
    if (!isInitializedFromUrlRef.current || isLoading || presentationBlocks.length === 0) {
      return;
    }

    if (isHashUpdateProgrammaticRef.current) {
      return;
    }

    const slideFromHash = getSlideFromHash();
    
    if (slideFromHash !== null && slideFromHash === currentSlide) {
      return;
    }

    if (currentSlide === 0 && slideFromHash !== null && slideFromHash > 0) {
      return;
    }

    if (slideFromHash !== currentSlide) {
      setSlideInHash(currentSlide, totalSlides);
    }
  }, [currentSlide, totalSlides, isLoading, presentationBlocks.length, setSlideInHash, getSlideFromHash]);

  useEffect(() => {
    if (!isMobile744 || !isSidebarOpen) {
      return;
    }
    setIsSidebarOpen(false);
  }, [isMobile744, isSidebarOpen]);

  useEffect(() => {
    document.querySelectorAll('audio').forEach((audio) => {
      if (!audio.paused) audio.pause();
    });
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fullscreenImageSrc) {
        if (e.key === 'Escape') {
          handleCloseFullscreen();
        }
        return;
      }

      const prevKeys = ['ArrowLeft', 'ArrowUp', 'PageUp'];
      const nextKeys = ['ArrowRight', 'ArrowDown', 'PageDown'];

      if (prevKeys.includes(e.key)) {
        e.preventDefault();
        if (canGoPrev) {
          handlePrev();
        }
        return;
      }

      if (nextKeys.includes(e.key)) {
        e.preventDefault();
        if (canGoNext) {
          handleNext();
        }
        return;
      }

      if (e.key === 'Escape' && close) {
        close(getCurrentSlideInfo());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlide, totalSlides, close, canGoPrev, canGoNext, fullscreenImageSrc, getCurrentSlideInfo, presentationBlocks.length]);

  if (isLoading) {
    return (
      <PresentationModeContainer backgroundColor={backgroundColor} onClick={(e) => e.stopPropagation()}>
        <EmptyStateContent>
          <FlexBox align="center" width={"100%"} justify="center" style={{ height: "calc(50vw - 64px)" }}>
            <LoaderSpinner text={'Загрузка...'} color="#909090" />
          </FlexBox>
          {close && (
            <ExitPresentationButton onClick={close}>
              Выйти из режима презентации
            </ExitPresentationButton>
          )}
        </EmptyStateContent>
      </PresentationModeContainer>
    );
  }

  if (presentationBlocks.length === 0) {
    return (
      <PresentationModeContainer backgroundColor={backgroundColor} onClick={(e) => e.stopPropagation()}>
        <EmptyStateContent>
          <div>Нет блоков для отображения</div>
          {close && (
            <ExitPresentationButton onClick={close}>
              Выйти из режима презентации
            </ExitPresentationButton>
          )}
        </EmptyStateContent>
      </PresentationModeContainer>
    );
  }

  return (
    <PresentationModeContainer backgroundColor={backgroundColor} isSidebarOpen={!isMobile744 && isSidebarOpen} onClick={(e) => e.stopPropagation()}>
      <DisablePullToRefresh />
      <HeightAdjustmentStyles />
      <Sidebar
        isOpen={!isMobile744 && isSidebarOpen}
        backgroundColor={backgroundColor}
        totalSlides={totalSlides}
        currentSlide={currentSlide}
        project={project}
        blocks={blocks}
        presentationBlocks={presentationBlocks}
        theme={theme}
        lang={i18n.language}
        onSlideClick={handleSlideClick}
      />

      <SliderContainer
        ref={sliderContainerRef}
        isSidebarOpen={!isMobile744 && isSidebarOpen}
        onTouchStart={handleMobileTouchStart}
        onTouchEnd={handleMobileTouchEnd}
        onClickCapture={handleImageClickCapture}
      >
        <Slider
          ref={sliderRef}
          {...SLIDER_SETTINGS}
          swipe={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          draggable={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          touchMove={!fullscreenImageSrc && !isInteractiveVideoSlide && !isMobile744}
          afterChange={(index) => setCurrentSlide(index)}
        >
          <SlideWrapper key="cover" isActive={currentSlide === 0}>
            <CoverSlide project={project} blocks={blocks} backgroundColor={backgroundColor} lang={i18n.language} isTeam={isTeam} />
          </SlideWrapper>

          {presentationBlocks.map((block, index) => {
            const slideIndex = index + 1;
            const isActive = currentSlide === slideIndex;
            return (
              <SlideWrapper key={block.postId || block.id || index} isActive={isActive}>
                <BlockContainer>
                  <LongreadBlockRenderer 
                    block={block} 
                    theme={theme} 
                    backgroundColor={backgroundColor} 
                    isActive={isActive}
                  />
                </BlockContainer>
              </SlideWrapper>
            );
          })}
        </Slider>
        <ButtonsWrapper isSidebarOpen={!isMobile744 && isSidebarOpen}>
          <Navigation
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            isMobile={isMobile744}
            onPrev={handlePrev}
            onNext={handleNext}
            onShrink={handleShrinkClick}
            onToggleSidebar={() => !isMobile744 && setIsSidebarOpen(!isSidebarOpen)}
          />
        </ButtonsWrapper>
        {fullscreenImageSrc && (
          <FullscreenImageOverlay onClick={handleCloseFullscreen}>
            <ModalClose
              isPreviewer
              mobileVersion={isMobile744}
              role="button"
              aria-label={t('close')}
              onClick={(e) => {
                e.stopPropagation();
                handleCloseFullscreen();
              }}
            >
              {isMobile744 ? <CloseModalIconMob /> : <CloseModalIcon />}
            </ModalClose>
            <FullscreenImage
              src={fullscreenImageSrc}
              alt={fullscreenImageAlt}
              onClick={(e) => e.stopPropagation()}
            />
          </FullscreenImageOverlay>
        )}
      </SliderContainer>
    </PresentationModeContainer>
  );
};
